import { useCallback, useRef } from 'react';
import type Handsontable from 'handsontable';

import { _isBlank, _isNil, _isNotEmpty, _notNil } from '@/littledash';
import type { CellValue } from '@/support/hot.types';
import { readInVivoValidatorResult } from '@/support/hotValidator.util';

const ERROR_TOOLTIP_ID = 'global-tooltip-id';
const INVALID_CLASS = 'htInvalid';

interface HotInstanceRef {
  current: { hotInstance?: unknown } | null;
}

interface UseTreatmentGridValidationParams {
  /** The grid's ref, so the hook can reach the instance outside of a hook callback. */
  hotRef: HotInstanceRef;
  setInvalid: (invalid: boolean) => void;
  /** Trailing spare rows that must never be reported as invalid. */
  spareRows?: number;
  /** Fallback tooltip text for a cell whose validator cannot supply a reason. */
  fallbackMessage: string;
}

/**
 * Narrows a ref's `hotInstance` to a usable grid. The ref is null until Handsontable finishes
 * constructing, and hooks that fire during construction are handed a half-built instance.
 */
export const isHotInstance = (hot: unknown): hot is Handsontable.Core =>
  _notNil(hot) && typeof (hot as Handsontable.Core).validateCells === 'function';

const cellKey = (row: number, col: number): string => `${row},${col}`;

const clearCellError = (cell: HTMLTableCellElement): void => {
  cell.removeAttribute('data-tooltip-id');
  cell.removeAttribute('data-tooltip-content');
  cell.removeAttribute('data-type');
  cell.classList.remove(INVALID_CLASS);
};

const markCellError = (cell: HTMLTableCellElement, message: string): void => {
  cell.setAttribute('data-tooltip-id', ERROR_TOOLTIP_ID);
  cell.setAttribute('data-tooltip-content', message);
  cell.setAttribute('data-type', 'error');
  cell.classList.add(INVALID_CLASS);
};

/**
 * Validation plumbing shared by the bulk treatment grids, which render Handsontable directly.
 *
 * Handsontable reports only *whether* a cell is valid, never why. The treatment name column is
 * validated by a hybrid validator (see `@/support/hotValidator.util`), which records its reason on
 * the cell's meta; this hook reads that back and stamps it onto the cell as the tooltip attributes
 * `Spreadsheet.scss` already styles.
 *
 * It also tracks which cells are currently invalid so the modal can disable its submit button,
 * and keeps that set honest as rows are removed, undone or redone.
 */
export const useTreatmentGridValidation = ({
  hotRef,
  setInvalid,
  spareRows = 0,
  fallbackMessage,
}: UseTreatmentGridValidationParams) => {
  const invalidCellsRef = useRef<Map<string, string>>(new Map());
  const setInvalidRef = useRef(setInvalid);
  setInvalidRef.current = setInvalid;
  const hotRefRef = useRef(hotRef);
  hotRefRef.current = hotRef;

  const publishInvalid = useCallback((): void => {
    setInvalidRef.current(invalidCellsRef.current.size > 0);
  }, []);

  const getHot = useCallback((): Handsontable.Core | undefined => {
    const hot = hotRefRef.current?.current?.hotInstance;
    return isHotInstance(hot) ? hot : undefined;
  }, []);

  /**
   * Whether a failing cell is a trailing blank Handsontable keeps for the next entry, which the
   * required-name rule would otherwise flag the moment the grid renders.
   *
   * The value is tested as well as the position, because the row that *becomes* a real row is
   * validated before Handsontable appends the next spare: at that moment the newly filled row is
   * still the last one, and exempting it on position alone would silently accept an invalid name.
   */
  const isExemptSpareRow = useCallback(
    (hot: Handsontable.Core, row: number, value: CellValue): boolean =>
      spareRows > 0 && row >= hot.countRows() - spareRows && _isBlank(value as string),
    [spareRows]
  );

  const revalidate = useCallback((): void => {
    const hot = getHot();
    if (_isNil(hot)) {
      return;
    }

    // Report the current tally up front: a grid holding only spare rows has nothing to validate,
    // so no `afterValidate` would fire and the modal would never learn its submit state.
    publishInvalid();

    if (spareRows > 0) {
      const rowsToValidate = Math.max(0, hot.countRows() - spareRows);
      hot.validateRows([...Array(rowsToValidate).keys()]);
      return;
    }
    hot.validateCells();
  }, [getHot, publishInvalid, spareRows]);

  const afterValidate = useCallback(
    function (this: Handsontable.Core, isValid: boolean, value: CellValue, row: number, prop: string | number) {
      const hot = isHotInstance(this) ? this : getHot();
      if (_isNil(hot)) {
        return;
      }

      const col = hot.propToCol(prop);
      if (typeof col !== 'number' || col < 0) {
        return;
      }

      const key = cellKey(row, col);
      const cell = hot.getCell(row, col);

      if (isValid || isExemptSpareRow(hot, row, value)) {
        invalidCellsRef.current.delete(key);
        if (_notNil(cell)) {
          clearCellError(cell);
        }
        publishInvalid();
        return;
      }

      // The validator recorded why it failed on the cell's meta; reading it back avoids re-running
      // the validator, which would repeat its uniqueness request. A cell validated by something
      // other than a hybrid validator records nothing, so it falls back to the generic message.
      const recorded = readInVivoValidatorResult(hot.getCellMeta(row, col), value);
      const message = _notNil(recorded) && !recorded.valid ? recorded.message : fallbackMessage;

      invalidCellsRef.current.set(key, message);
      publishInvalid();
      if (_notNil(cell)) {
        markCellError(cell, message);
      }
    },
    [getHot, isExemptSpareRow, publishInvalid, fallbackMessage]
  );

  /**
   * Re-checks the *other* rows of the name column after an edit. Uniqueness is a property of the
   * column rather than of one cell, so resolving one half of a duplicate pair must clear the other
   * half too.
   *
   * The edited rows are deliberately excluded: Handsontable has already validated them itself, and
   * validating them again here would repeat the uniqueness request their validator makes.
   */
  const afterChange = useCallback(
    function (this: Handsontable.Core, changes: Array<Handsontable.CellChange> | null, source: string) {
      if (_isNil(changes) || source === 'loadData') {
        return;
      }
      const hot = isHotInstance(this) ? this : getHot();
      if (_isNil(hot)) {
        return;
      }

      const editedRows = changes.reduce<Set<number>>((rows, change) => {
        if (_notNil(change)) {
          rows.add(change[0]);
        }
        return rows;
      }, new Set());

      const rowsToRecheck = [...Array(Math.max(0, hot.countRows() - spareRows)).keys()].filter(
        (row) => !editedRows.has(row)
      );

      if (_isNotEmpty(rowsToRecheck)) {
        hot.validateRows(rowsToRecheck);
      }
    },
    [getHot, spareRows]
  );

  /**
   * Drops every recorded error and re-derives them from the grid. Row indices shift when a row is
   * removed or an edit is undone, so keys recorded against the old layout describe the wrong cells.
   */
  const resetAndRevalidate = useCallback((): void => {
    invalidCellsRef.current.clear();
    publishInvalid();
    revalidate();
  }, [publishInvalid, revalidate]);

  /**
   * Restores error attributes onto a cell Handsontable has just repainted. A repaint hands back a
   * bare `td`, so without this a scroll or re-render would silently drop the tooltip and styling.
   */
  const afterRenderer = useCallback((cell: HTMLTableCellElement, row: number, col: number): void => {
    const message = invalidCellsRef.current.get(cellKey(row, col));
    if (_isNil(message)) {
      clearCellError(cell);
      return;
    }
    markCellError(cell, message);
  }, []);

  return {
    afterValidate,
    afterChange,
    afterRemoveRow: resetAndRevalidate,
    afterUndoRedo: resetAndRevalidate,
    afterRenderer,
    revalidate,
  };
};
